import { shouldRekickTranscriptLoad } from '@src/utility/transcriptLoadRetry';
import { useEffect, useRef } from 'react';

type Params = {
  storyId: string | null;
  /** The story carries a transcript to fetch (see `storyHasTranscript`). */
  hasTranscript: boolean;
  /** `TranscriptionStore.isTranscriptionReady` */
  isReady: boolean;
  /** `TranscriptionStore.isTranscriptionLoading` */
  isLoading: boolean;
  /** `videoCreator.loadTranscriptIfNeeded` */
  load: () => Promise<unknown>;
};

/**
 * PLAT-11697: a story opened mid-pending on the Sidecar Path can sit on
 * "Preparing transcript data…" for ever — TranscriptionStore marks a failed
 * first load Failed and ContentStudio's one-shot kick never re-fires. Retry
 * once per story when the transcript exists but the store never loaded it;
 * the ContentView mid-pending poll owns any later (bounded) retries.
 */
export function useTranscriptLoadRekick({
  storyId,
  hasTranscript,
  isReady,
  isLoading,
  load,
}: Params): void {
  const rekickedForStoryIdRef = useRef<string | null>(null);
  // Latest `load` without making it an effect dependency (hosts pass a fresh
  // arrow each render).
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!storyId || rekickedForStoryIdRef.current === storyId) return;
    if (
      !shouldRekickTranscriptLoad({
        hasElementsUrl: hasTranscript,
        isReady,
        isLoading,
      })
    ) {
      return;
    }
    rekickedForStoryIdRef.current = storyId;
    void loadRef.current();
  }, [storyId, hasTranscript, isReady, isLoading]);
}
